import { datadogRum } from '@datadog/browser-rum';

import {
  SettingsGroup,
  SettingsPageFrame,
  usePrivacyModeToggle,
} from '@eluve/blocks';
import {
  Button,
  Field,
  FieldContent,
  FieldDescription,
  FieldLabel,
  FormHeader,
  FormNewDescription,
  FormSection,
  FormTitle,
  Icon,
  Kbd,
  Separator,
  Stack,
  Switch,
  Text,
} from '@eluve/components';
import {
  EnableSoundEffects,
  InterfaceLanguageSelector,
  useTenantUserSettings,
} from '@eluve/frontend-feature-user-settings';
import { useTranslation } from '@eluve/i18n';
import { useAnalytics } from '@eluve/posthog-react';
import {
  useAssignedTenantIdFromParams,
  useUserIdFromSession,
} from '@eluve/session-helpers';
import { MicrophonePermissionProvider } from '@eluve/smart-blocks';
import { sessionSoundPlayer } from '@eluve/sounds';
import { useTenantLocalStorage } from '@eluve/utility-hooks';

import { useShowAppointmentsOwner } from '../../../../settings/hooks/useShowAppointmentsOwner';
import { MicSettings } from '../MicSettings';

const advancedMonitoringThresholdHours = 48;

export const AudioDisplaySettingsPage: React.FC = () => {
  const tenantId = useAssignedTenantIdFromParams();
  const userId = useUserIdFromSession();
  const analytics = useAnalytics('user_settings');
  const { t } = useTranslation();
  const { setPrivacyMode, privacyMode } = usePrivacyModeToggle();
  const [showAppointmentsOwner, setShowAppointmentsOwner] =
    useShowAppointmentsOwner();
  const { settings } = useTenantUserSettings();

  const [enabledAdvancedMonitoring, setEnabledAdvancedMonitoring] =
    useTenantLocalStorage({
      initialValue: false,
      key: 'ADVANCED_MONITORING',
      tenantId,
      ttlInSeconds: 60 * 60 * advancedMonitoringThresholdHours,
    });

  const previewSound = () => {
    // Force-play so the preview also works while the toggle is off.
    sessionSoundPlayer.setIsEnabled(true);
    sessionSoundPlayer.play('start');
    sessionSoundPlayer.setIsEnabled(settings?.enableSoundEffects ?? true);
  };

  return (
    <SettingsPageFrame title={t('settings.nav.audioDisplay')}>
      <FormSection className="gap-4">
        <FormHeader>
          <FormTitle>{t('settings.audio.microphone')}</FormTitle>
          <FormNewDescription>
            {t('settings.audio.microphoneDescription')}
          </FormNewDescription>
        </FormHeader>
        <MicrophonePermissionProvider>
          <MicSettings />
        </MicrophonePermissionProvider>
      </FormSection>

      <SettingsGroup title={t('settings.audio.notifications')}>
        <Field orientation="horizontal" className="px-6 py-7">
          <FieldContent>
            <FieldLabel>{t('settings.audio.soundEffect')}</FieldLabel>
            <FieldDescription>
              {t('settings.audio.soundEffectDescription')}
            </FieldDescription>
          </FieldContent>
          <EnableSoundEffects />
        </Field>
        <Separator className="my-0" />
        <Field orientation="horizontal" className="px-6 py-7">
          <FieldLabel>{t('settings.audio.previewSound')}</FieldLabel>
          <Button variant="outline" size="sm" onClick={previewSound}>
            <Icon name="Play" size="auto" />
            {t('settings.audio.play')}
          </Button>
        </Field>
      </SettingsGroup>

      <SettingsGroup title={t('settings.audio.language')}>
        <Field orientation="horizontal" className="px-6 py-7">
          <FieldContent>
            <FieldLabel>
              {t('settings.audio.interfaceLanguageLabel')}
            </FieldLabel>
            <FieldDescription>
              {t('settings.audio.interfaceLanguageDescription')}
            </FieldDescription>
          </FieldContent>
          <div className="flex w-full max-w-60 justify-end">
            <InterfaceLanguageSelector />
          </div>
        </Field>
      </SettingsGroup>

      <SettingsGroup title={t('settings.audio.privacyDisplay')}>
        <Stack gap={3} align="stretch" className="px-6 py-7">
          <Field orientation="horizontal">
            <FieldContent>
              <FieldLabel>{t('settings.audio.privacyMode')}</FieldLabel>
              <FieldDescription>
                {t('settings.audio.privacyModeDescription')}{' '}
                {t('settings.audio.privacyModeToggleHintPrefix')}{' '}
                <Kbd>Shift</Kbd> + <Kbd>P</Kbd>.
              </FieldDescription>
            </FieldContent>
            <Switch
              checked={privacyMode}
              onCheckedChange={(checked) => {
                setPrivacyMode(!checked);
                analytics.capture('enable_privacy_mode_update', {
                  tenantId,
                  userId,
                  checked: !checked,
                });
              }}
              size="sm"
              className="data-unchecked:bg-border-tertiary"
              aria-label={t('settings.audio.privacyMode')}
            />
          </Field>
          <Text size="xs" color="tertiary" className="italic">
            {t('settings.audio.examplePatientName')}{' '}
            <span className="-m-2 inline-block p-2 font-semibold privacy-text">
              John Doe
            </span>
          </Text>
        </Stack>
        <Separator className="my-0" />
        <Field orientation="horizontal" className="px-6 py-7">
          <FieldContent>
            <FieldLabel>{t('settings.audio.showAppointmentOwners')}</FieldLabel>
            <FieldDescription>
              {t('settings.audio.showAppointmentOwnersDescription')}
            </FieldDescription>
          </FieldContent>
          <Switch
            checked={showAppointmentsOwner}
            onCheckedChange={(checked) => {
              setShowAppointmentsOwner(checked);
              analytics.capture('show_owner_appointments_update', {
                tenantId,
                userId,
                checked,
              });
            }}
            size="sm"
            className="data-unchecked:bg-border-tertiary"
            aria-label={t('settings.audio.showAppointmentOwners')}
          />
        </Field>
        <Separator className="my-0" />
        <Field orientation="horizontal" className="px-6 py-7">
          <FieldContent>
            <FieldLabel>
              {t('settings.audio.shareScreenWithSupport')}
            </FieldLabel>
            <FieldDescription className="text-balance">
              {t('settings.audio.shareScreenDescription', {
                hours: advancedMonitoringThresholdHours,
              })}
            </FieldDescription>
          </FieldContent>
          <Switch
            checked={Boolean(enabledAdvancedMonitoring)}
            onCheckedChange={(checked) => {
              setEnabledAdvancedMonitoring(checked);
              datadogRum.setTrackingConsent(
                checked ? 'granted' : 'not-granted',
              );
              analytics.capture('enable_advanced_monitoring_update', {
                tenantId,
                userId,
                checked,
              });
            }}
            size="sm"
            className="data-unchecked:bg-border-tertiary"
            aria-label={t('settings.audio.shareScreenWithSupport')}
          />
        </Field>
      </SettingsGroup>
    </SettingsPageFrame>
  );
};
